import type { QueryClient } from "@tanstack/react-query";
import { createRouter, type RouterHistory } from "@tanstack/react-router";

import { ApiError } from "./api/client";
import { endSession, sessionQueryOptions } from "./api/session";
import { DefaultErrorComponent, DefaultPendingComponent, NotFound } from "./components/status";
import { routeTree } from "./routeTree.gen";

export interface CreateAppRouterOptions {
	readonly queryClient: QueryClient;
	/** Tests pass a memory history; the browser uses the default. */
	readonly history?: RouterHistory;
}

export function createAppRouter({ queryClient, history }: CreateAppRouterOptions) {
	const router = createRouter({
		routeTree,
		history,
		context: { queryClient },
		defaultPreload: "intent",
		// Loaders read through the Query cache, so React Query owns staleness.
		defaultPreloadStaleTime: 0,
		scrollRestoration: true,
		defaultPendingComponent: DefaultPendingComponent,
		defaultErrorComponent: DefaultErrorComponent,
		defaultNotFoundComponent: NotFound,
	});
	// A 401 from the API means the session has ended, for example because the member was removed or the refresh token
	// expired: forget everything cached and send the visitor to sign in again, then back to where they were.
	queryClient.getQueryCache().subscribe(event => {
		const hadSession = queryClient.getQueryData(sessionQueryOptions().queryKey) != undefined;
		if (
			!hadSession ||
			event.type !== "updated" ||
			event.action.type !== "error" ||
			!(event.action.error instanceof ApiError) ||
			event.action.error.status !== 401
		) {
			return;
		}
		endSession(queryClient);
		void router.navigate({ to: "/login", search: { redirect: router.state.location.href } });
	});
	return router;
}

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof createAppRouter>;
	}
	interface StaticDataRouteOption {
		/** Breadcrumb trail shown after the home icon in the top bar; omit for none. */
		crumbs?: readonly string[];
		/** Signed-out visitors can open the route, and it renders without the app shell (login and sign-up pages). */
		public?: boolean;
	}
}
